import {
  EdgelessFrameManagerIdentifier,
  type NavigatorMode,
  PresentTool,
} from '@blocksuite/affine-block-frame';
import { EdgelessLegacySlotIdentifier } from '@blocksuite/affine-block-surface';
import {
  DocModeProvider,
  EditPropsStore,
} from '@blocksuite/affine-shared/services';
import {
  STREAMLAIN_COMPACT_HEADER_AREA_LIT_CSS,
  STREAMLAIN_HEADER_BORDER_WIDTH,
  STREAMLAIN_PANEL_SETTINGS_BUTTON_CLASS,
  STREAMLAIN_PANEL_SETTINGS_BUTTON_STYLES,
} from '@blocksuite/affine-shared/styles';
import { createButtonPopper } from '@blocksuite/affine-shared/utils';
import { streamLainDisclosureStyles } from '@blocksuite/affine-shared/utils';
import { DisposableGroup } from '@blocksuite/global/disposable';
import { WithDisposable } from '@blocksuite/global/lit';
import { PresentationIcon, SettingsIcon } from '@blocksuite/icons/lit';
import type { EditorHost } from '@blocksuite/std';
import { GfxControllerIdentifier } from '@blocksuite/std/gfx';
import { cssVarV2 } from '@streamlain/theme/v2';
import { css, html, LitElement, type PropertyValues, unsafeCSS } from 'lit';
import { property, query, state } from 'lit/decorators.js';

const styles = css`
  :host {
    display: flex;
    width: 100%;
    justify-content: start;
  }

  .frame-panel-header {
    ${unsafeCSS(STREAMLAIN_COMPACT_HEADER_AREA_LIT_CSS)}
    justify-content: space-between;
    border-bottom: ${unsafeCSS(STREAMLAIN_HEADER_BORDER_WIDTH)} solid
      ${unsafeCSS(cssVarV2('layer/insideBorder/border'))};
  }

  ${unsafeCSS(streamLainDisclosureStyles)}
  ${unsafeCSS(STREAMLAIN_PANEL_SETTINGS_BUTTON_STYLES)}

  .all-frames-setting {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
  }

  .all-frames-setting-label {
    flex: 0 1 auto;
    min-width: 0;
    font-size: var(--streamlain-font-sm);
    font-weight: 500;
    line-height: 22px;
    color: var(--light-text-color-text-secondary-color, #8e8d91);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .frames-setting-container {
    display: none;
    justify-content: center;
    align-items: center;
  }

  .frames-setting-container[data-show] {
    display: flex;
  }

  .presentation-button {
    display: flex;
    align-items: center;
    gap: 4px;
    box-sizing: border-box;
    width: 117px;
    height: 28px;
    padding: 4px 8px;
    border-radius: 8px;
    margin: 0;
    border: 1px solid var(--streamlain-v2-layer-insideBorder-border);
    /* E016-S003: Use theme-aware variable instead of hardcoded --streamlain-v2-layer-white */
    background: var(--streamlain-v2-layer-background-secondary);
    flex: 0 0 auto;
  }

  .presentation-button:hover {
    background: var(--streamlain-v2-layer-background-hoverOverlay);
    cursor: pointer;
  }

  .presentation-button svg {
    fill: var(--streamlain-v2-icon-primary);
    margin-right: 4px;
  }

  .presentation-button-label {
    font-size: 12px;
    font-weight: 500;
    line-height: 20px;
  }
`;

export const AFFINE_FRAME_PANEL_HEADER = 'affine-frame-panel-header';

export interface FramePanelHeaderLabels {
  allFrames: string;
  settings: string;
  presentation: string;
  canvasSettings: string;
  showFrames: string;
  previewSettings: string;
  fillScreen: string;
  playbackSettings: string;
  darkBackground: string;
  hideToolbar: string;
}

export const DEFAULT_FRAME_PANEL_HEADER_LABELS: FramePanelHeaderLabels = {
  allFrames: 'Frames',
  settings: 'Frame settings',
  presentation: 'Presentation',
  canvasSettings: 'Canvas settings',
  showFrames: 'Show frames',
  previewSettings: 'Preview settings',
  fillScreen: 'Fill screen',
  playbackSettings: 'Playback settings',
  darkBackground: 'Dark background',
  hideToolbar: 'Hide toolbar',
};

export class FramePanelHeader extends WithDisposable(LitElement) {
  static override styles = styles;

  private readonly _clearEdgelessDisposables = () => {
    this._edgelessDisposables?.dispose();
    this._edgelessDisposables = null;
  };

  private _edgelessDisposables: DisposableGroup | null = null;

  private get _gfx() {
    return this.editorHost.std.get(GfxControllerIdentifier);
  }

  private readonly _enterPresentationMode = () => {
    const docModeProvider = this.editorHost.std.get(DocModeProvider);
    if (docModeProvider.getEditorMode() !== 'edgeless') {
      this.editorHost.std.get(DocModeProvider).setEditorMode('edgeless');
    }

    setTimeout(() => {
      this.editorHost.std
        .get(EdgelessFrameManagerIdentifier)
        .loadFrames()
        .then(() => {
          this._gfx.tool.setTool(PresentTool, {
            mode: this._navigatorMode,
          });
        })
        .catch(console.error);
    }, 100);
  };

  private _framesSettingMenuPopper: ReturnType<
    typeof createButtonPopper
  > | null = null;

  private _navigatorMode: NavigatorMode = 'fit';

  private readonly _setEdgelessDisposables = () => {
    const slots = this.editorHost.std.get(EdgelessLegacySlotIdentifier);

    this._clearEdgelessDisposables();
    this._edgelessDisposables = new DisposableGroup();
    this._edgelessDisposables.add(
      slots.navigatorSettingUpdated.subscribe(({ fillScreen }) => {
        this._navigatorMode = fillScreen ? 'fill' : 'fit';
      })
    );
  };

  private _tryLoadNavigatorStateLocalRecord() {
    this._navigatorMode = this.editorHost.std
      .get(EditPropsStore)
      .getStorage('presentFillScreen')
      ? 'fill'
      : 'fit';
  }

  override connectedCallback() {
    super.connectedCallback();
    this._tryLoadNavigatorStateLocalRecord();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (this._edgelessDisposables) {
      this._clearEdgelessDisposables();
    }
  }

  override firstUpdated() {
    const disposables = this.disposables;

    this._framesSettingMenuPopper = createButtonPopper({
      reference: this._frameSettingButton,
      popperElement: this._frameSettingMenu,
      stateUpdated: ({ display }) => {
        this._settingPopperShow = display === 'show';
      },
      mainAxis: 14,
      crossAxis: -100,
    });
    disposables.add(this._framesSettingMenuPopper);
  }

  override render() {
    return html`<div class="frame-panel-header">
      <div class="all-frames-setting">
        <span class="all-frames-setting-label">${this.labels.allFrames}</span>
        <edgeless-tool-icon-button
          class="${STREAMLAIN_PANEL_SETTINGS_BUTTON_CLASS} all-frames-setting-button ${this
            ._settingPopperShow
            ? 'active'
            : ''}"
          .tooltip=${this._settingPopperShow ? '' : this.labels.settings}
          .tipPosition=${'top'}
          .active=${this._settingPopperShow}
          .activeMode=${'background'}
          @click=${() => this._framesSettingMenuPopper?.toggle()}
        >
          ${SettingsIcon({ width: '20px', height: '20px' })}
        </edgeless-tool-icon-button>
      </div>
      <div class="frames-setting-container streamlain-disclosure-popover-shell">
        <affine-frames-setting-menu
          .editorHost=${this.editorHost}
          .labels=${this.labels}
        ></affine-frames-setting-menu>
      </div>
      <div class="presentation-button" @click=${this._enterPresentationMode}>
        ${PresentationIcon({ width: '16px', height: '16px' })}<span
          class="presentation-button-label"
          >${this.labels.presentation}</span
        >
      </div>
    </div>`;
  }

  override updated(_changedProperties: PropertyValues) {
    if (_changedProperties.has('editorHost')) {
      const docModeProvider = this.editorHost.std.get(DocModeProvider);
      if (docModeProvider.getEditorMode() === 'edgeless') {
        this._setEdgelessDisposables();
      } else {
        this._clearEdgelessDisposables();
      }
    }
  }

  @query('.all-frames-setting-button')
  private accessor _frameSettingButton!: HTMLDivElement;

  @query('.frames-setting-container')
  private accessor _frameSettingMenu!: HTMLDivElement;

  @state()
  private accessor _settingPopperShow = false;

  @property({ attribute: false })
  accessor editorHost!: EditorHost;

  @property({ attribute: false })
  accessor labels: FramePanelHeaderLabels = DEFAULT_FRAME_PANEL_HEADER_LABELS;
}

declare global {
  interface HTMLElementTagNameMap {
    [AFFINE_FRAME_PANEL_HEADER]: FramePanelHeader;
  }
}
